// Language-wise voice audio resolver for browser-played cues (e.g. the campaign
// invite audio on the create screen).
//
// This branch plays the scan-flow voice cues natively (React Native), so it does
// NOT ship the in-browser scan speech engine. This module is intentionally
// small: it only fetches the per-language voice manifest
// (POST /res/get_voice_audio_files -> { status, files: { <cue>: <mp3 url> } })
// and resolves a single cue's URL, so web screens can play a localized clip with
// a bundled fallback.

import storage from '../Assets/js/Global';
import { urls } from '../Url';

const COMPANY_KEY = process.env.REACT_APP_COMPANY_ID || '';

// Per-language manifest cache: lang -> Promise<manifest | null>. Each language is
// fetched at most once for the lifetime of the page.
const manifestCache = {};

function fetchManifest(lang) {
  const target = String(lang || storage.get('selectedLanguage') || 'en').toLowerCase();
  if (manifestCache[target]) return manifestCache[target];
  manifestCache[target] = fetch(urls.getVoiceAudioFiles, {
    method: 'POST',
    credentials: 'omit',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ companyKey: COMPANY_KEY, lang: target }),
  })
    .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
    .then((data) => {
      if (!data || data.status !== true || !data.files) {
        throw new Error('malformed voice manifest');
      }
      return data;
    })
    .catch((err) => {
      console.warn(`[voiceAudio] manifest load failed for "${target}":`, err?.message || err);
      delete manifestCache[target]; // permit a retry later
      return null;
    });
  return manifestCache[target];
}

// Resolve a single cue's URL for a language (e.g. "invite"). Resolves to null if
// the cue/language is unavailable so callers can fall back to a bundled file.
export function getVoiceUrl(key, lang) {
  return fetchManifest(lang).then((data) => (data && data.files ? data.files[key] || null : null));
}

// Resolve the full cue -> url map for a language. Shares the same per-language
// cache as getVoiceUrl, so callers that need every clip (e.g. the native scan
// audio bridge) hit the endpoint at most once per language. Resolves to null on
// failure so callers keep whatever they already had.
export function getVoiceFiles(lang) {
  return fetchManifest(lang).then((data) => (data && data.files ? data.files : null));
}

// Warm the per-language manifest cache. Idempotent per language via
// manifestCache: safe to call on every language change; it hits the API only the
// first time a given language is requested. Call once after login / on language
// change so screens resolve their audio instantly from cache instead of paying
// the get_voice_audio_files round-trip on mount.
export function prefetchVoiceManifest(lang) {
  return fetchManifest(lang);
}
